import { useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import ErrorI from '@material-ui/icons/ErrorOutline'
import { useAppSelector } from 'store/hooks'
import useDialog from 'hooks/Shared/useDialog'
import { IPaginatedData } from 'models/shared.models'
import { QueryOptions } from 'models/general'
import Employee from 'models/employee'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import { selectSelectedEmployee } from 'lib/helpers/selectors'
import {
  setEmployeesList,
  setPaginationValue,
  setSelectedEmployee,
} from 'actions/employees'
import { getEmployeeById, getEmployeesList } from 'services/employees'
import { TModalIcon } from 'components/Shared/SydDialog/SydDialog'

const useEmployeesList = (
  workspaceId: number,
  page: number,
  size: number,
  search?: string,
  options: QueryOptions<IPaginatedData<Employee>> = {}
) => {
  const dispatch = useDispatch()
  const dialog = useDialog()

  const {
    onBefore,
    onSuccess,
    onError,
    noSave = false,
    noDialog = false,
    refreshOn = [],
    ...rest
  } = options

  const query = useQuery<IPaginatedData<Employee>, any>(
    ['get-employees-list', ...refreshOn],
    () => {
      onBefore && onBefore()
      return getEmployeesList(workspaceId, page, size, search)
    },
    {
      ...rest,
      onSuccess: data => {
        !noSave && dispatch(setEmployeesList(data.list))
        !noSave && dispatch(setPaginationValue('total', data.total))
        onSuccess && onSuccess(data)
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la lista de Contadores.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}

export const useEmployee = (
  workspaceId: number,
  employeeId: number,
  options: QueryOptions<Employee> = {}
) => {
  const dispatch = useDispatch()
  const dialog = useDialog()

  const employee = useAppSelector(selectSelectedEmployee) || undefined

  const {
    onBefore,
    onSuccess,
    onError,
    noSave = false,
    noDialog = false,
    refreshOn = [],
    ...rest
  } = options

  const query = useQuery<Employee, any>(
    ['get-employee', employeeId, ...refreshOn],
    () => {
      onBefore && onBefore()
      return getEmployeeById(workspaceId, employeeId)
    },
    {
      ...rest,
      onSuccess: data => {
        !noSave &&
          !!employee &&
          dispatch(
            setSelectedEmployee({
              ...employee,
              assignedWorkspaces: data.assignedWorkspaces,
            })
          )
        !noSave && !employee && dispatch(setSelectedEmployee(data))
        onSuccess && onSuccess(data)
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la información del contador.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}

export default useEmployeesList
